import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, Wand2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription } from 'shadcn/components/ui/Card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'shadcn/components/ui/Collapsible';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { cn } from 'shadcn/lib/utils';

import { parseNonNegativeNumCases } from './palletBuilderUtils';

const NOOP = () => {};

/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 *
 * @typedef {Object} QuickFillValues
 * @property {number} [companySourceId]
 * @property {number} [numCases]
 * @property {string} [expiration]
 * @property {string} [supplierLot]
 */

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {CompanySource[]} [props.companySourceOptions]
 * @param {boolean} [props.defaultOpen]
 * @param {boolean} [props.isFbmMode]
 * @param {(values: QuickFillValues) => void} [props.onApplyToAllPallets]
 */
function PalletQuickFillCard({
    className,
    companySourceOptions = [],
    defaultOpen = false,
    isFbmMode = false,
    onApplyToAllPallets = NOOP,
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [companySourceId, setCompanySourceId] = useState('');
    const [numCases, setNumCases] = useState('');
    const [expiration, setExpiration] = useState('');
    const [supplierLot, setSupplierLot] = useState('');

    const lotLabel = isFbmMode ? 'Partner Lot' : 'Supplier Lot';

    useEffect(() => {
        if (isFbmMode) {
            return;
        }

        if (companySourceOptions.length === 1) {
            setCompanySourceId(String(companySourceOptions[0].id));
            return;
        }

        setCompanySourceId((current) => {
            const isCurrentOptionValid = companySourceOptions.some((source) => String(source.id) === current);
            return isCurrentOptionValid ? current : '';
        });
    }, [companySourceOptions, isFbmMode]);

    const handleApply = useCallback(() => {
        /** @type {QuickFillValues} */
        const values = {};

        if (companySourceId) {
            values.companySourceId = Number(companySourceId);
        }
        const parsedNumCases = parseNonNegativeNumCases(numCases);
        if (parsedNumCases !== undefined) {
            values.numCases = parsedNumCases;
        }
        if (expiration) {
            values.expiration = expiration;
        }
        if (supplierLot.trim()) {
            values.supplierLot = supplierLot.trim();
        }

        onApplyToAllPallets(values);
    }, [companySourceId, expiration, numCases, onApplyToAllPallets, supplierLot]);

    return (
        <Card className={cn('gap-0 border bg-muted/30 py-0 shadow-none', className)}>
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className={cn('px-3', isOpen ? 'pt-2' : 'py-1')}>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="flex h-6 w-full items-center gap-1.5 rounded-sm text-left transition-colors hover:bg-accent/50"
                        >
                            <ChevronRight
                                aria-hidden="true"
                                className={cn(
                                    'size-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
                                    isOpen && 'rotate-90',
                                )}
                            />
                            <Wand2 aria-hidden="true" className="size-3.5 shrink-0 text-[#4C884B]" />
                            <span className="text-sm leading-none font-semibold">Quick fill</span>
                        </button>
                    </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                    <CardDescription className="px-3 pb-2 text-xs text-muted-foreground">
                        For simple receipts, set the shared values once and apply them. Only filled fields are applied.
                    </CardDescription>
                    <CardContent className="px-3 pt-0 pb-3">
                        <div className="flex flex-wrap items-end gap-2">
                            {!isFbmMode ? (
                                <div className="flex min-w-[120px] flex-[1.2] flex-col gap-1">
                                    <Label
                                        className="text-xs leading-none text-muted-foreground"
                                        htmlFor="quick-fill-source"
                                    >
                                        Source
                                    </Label>
                                    <Select value={companySourceId} onValueChange={setCompanySourceId}>
                                        <SelectTrigger id="quick-fill-source" className="w-full" size="sm">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companySourceOptions.map((source) => (
                                                <SelectItem key={source.id} value={String(source.id)}>
                                                    {source.displayName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}
                            <div className="flex min-w-[72px] flex-[0.6] flex-col gap-1">
                                <Label
                                    className="text-xs leading-none text-muted-foreground"
                                    htmlFor="quick-fill-num-cases"
                                >
                                    Cases
                                </Label>
                                <Input
                                    id="quick-fill-num-cases"
                                    className="h-8 text-sm"
                                    min={0}
                                    type="number"
                                    value={numCases}
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        if (raw === '') {
                                            setNumCases('');
                                            return;
                                        }
                                        const parsed = Number(raw);
                                        if (!Number.isFinite(parsed) || parsed < 0) {
                                            return;
                                        }
                                        setNumCases(raw);
                                    }}
                                />
                            </div>
                            <div className="flex min-w-[120px] flex-1 flex-col gap-1">
                                <Label
                                    className="text-xs leading-none text-muted-foreground"
                                    htmlFor="quick-fill-expiration"
                                >
                                    Expiration
                                </Label>
                                <Input
                                    id="quick-fill-expiration"
                                    className="h-8 text-sm"
                                    type="date"
                                    value={expiration}
                                    onChange={(event) => setExpiration(event.target.value)}
                                />
                            </div>
                            <div className="flex min-w-[100px] flex-1 flex-col gap-1">
                                <Label className="text-xs leading-none text-muted-foreground" htmlFor="quick-fill-lot">
                                    {lotLabel}
                                </Label>
                                <Input
                                    id="quick-fill-lot"
                                    className="h-8 text-sm"
                                    placeholder="Lot #"
                                    value={supplierLot}
                                    onChange={(event) => setSupplierLot(event.target.value)}
                                />
                            </div>
                            <Button
                                className="h-8 shrink-0 bg-[#4C884B] px-3 text-xs text-white hover:bg-[#4C884B]/90"
                                size="sm"
                                type="button"
                                onClick={handleApply}
                            >
                                Apply to all pallets
                            </Button>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

export { NOOP, PalletQuickFillCard };
